import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { rem, vwDesktop, vwMobile } from "../../helpers/styles";
import { BREAKPOINTS, MEDIA_DESKTOP } from "../../helpers/theme";
import SectionLines from "../SectionLines";
import { PauseIcon, PlayIcon } from "../shared/MediaControlIcons";

export type HeroSectionProps = {
  desktopBackgroundVideoSrc?: string;
  mobileBackgroundVideoSrc?: string;
  scrollCtaLabel: string;
  scrollTargetId: string;
};

const HeroSection: FC<HeroSectionProps> = ({
  mobileBackgroundVideoSrc,
  desktopBackgroundVideoSrc,
  scrollCtaLabel,
  scrollTargetId,
}: HeroSectionProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isPlayingRef = useRef(true);

  const sectionId = useMemo(
    () => `hero-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const [isPlaying, setIsPlaying] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0); // 0..1

  // Respect reduced motion
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (prefersReducedMotion) {
      el.pause();
      setIsPlaying(false);
      setVideoProgress(0);
      return;
    }

    const p = el.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        setIsPlaying(false);
      });
    }
  }, [prefersReducedMotion]);

  // Progress ring
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!isPlaying || prefersReducedMotion) return;

    let raf = 0;

    const tick = () => {
      if (!isPlayingRef.current) return;

      const duration = el.duration;
      const current = el.currentTime;

      if (Number.isFinite(duration) && duration > 0) {
        const next = Math.min(1, Math.max(0, current / duration));
        setVideoProgress(next);
      } else {
        setVideoProgress(0);
      }

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [isPlaying, prefersReducedMotion]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onLoaded = () => setVideoProgress(0);
    el.addEventListener("loadedmetadata", onLoaded);
    return () => el.removeEventListener("loadedmetadata", onLoaded);
  }, [mobileBackgroundVideoSrc, desktopBackgroundVideoSrc]);

  const togglePlayback = async () => {
    const el = videoRef.current;
    if (!el) return;

    if (el.paused) {
      try {
        await el.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  const onScrollCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(scrollTargetId);
    if (!target) return;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  return (
    <Section id={sectionId} aria-label={`Homepage`}>
      <BackgroundVideo
        ref={videoRef}
        autoPlay={!prefersReducedMotion}
        muted
        loop
        playsInline
        preload="auto"
      >
        {/* Mobile  */}
        <source
          src={mobileBackgroundVideoSrc}
          type="video/mp4"
          media="(max-width: 767px)"
        />
        {/* Desktop */}
        <source
          src={desktopBackgroundVideoSrc}
          type="video/mp4"
          media="(min-width: 768px)"
        />
      </BackgroundVideo>

      <Overlay aria-hidden="true" />
      <Center>
        <VisuallyHidden>
          Welcome to Beyond, the Plant Protein Company
        </VisuallyHidden>
      </Center>

      <ScrollCtaWrap>
        <ScrollCta href={`#${scrollTargetId}`} onClick={onScrollCtaClick}>
          {scrollCtaLabel}
        </ScrollCta>
      </ScrollCtaWrap>
      <PlayPauseWrap>
        <PlayPauseButton
          type="button"
          onClick={togglePlayback}
          aria-label={
            isPlaying ? "Pause background video" : "Play background video"
          }
          aria-pressed={!isPlaying}
        >
          <ProgressRing
            value={videoProgress}
            viewBox="0 0 43 43"
            aria-hidden="true"
          >
            <circle cx="21.5" cy="21.5" r="21" />
          </ProgressRing>

          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayPauseButton>
      </PlayPauseWrap>
    </Section>
  );
};

export default HeroSection;

const Section = styled.section`
  position: relative;
  width: 100%;
  height: calc(100svh - ${rem(104)});
  height: calc(100dvh - ${rem(104)});
  overflow: hidden;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    height: calc(100svh - ${rem(60)});
    height: calc(100dvh - ${rem(60)});
  }
  background: #f6f1e7;
`;
const VisuallyHidden = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;
const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* fills full hero */
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Center = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100svh;
  display: grid;
  place-items: center;

  ${SectionLines("#FFFBF4")};
`;

const ScrollCtaWrap = styled.div`
  position: absolute;
  bottom: ${vwMobile(40)};
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;

  ${MEDIA_DESKTOP} {
    bottom: ${vwDesktop(42)};
  }
`;
const PlayPauseWrap = styled.div`
  position: absolute;
  bottom: ${vwMobile(40)};
  right: ${vwMobile(24)};
  z-index: 3;

  ${MEDIA_DESKTOP} {
    bottom: ${vwDesktop(42)};
    right: ${vwDesktop(71)};
  }
`;

const ScrollCta = styled.a`
  color: #012604;
  text-align: center;
  font-family: "GT Alpina";
  font-style: normal;
  font-weight: 300;
  letter-spacing: ${rem(-0.726)};
  text-decoration: none;

  font-size: ${rem(14.514)};
  line-height: normal;

  display: inline-flex;
  height: ${rem(48)};
  padding: ${vwMobile(20)} ${vwMobile(32)};
  justify-content: center;
  align-items: center;

  border-radius: ${rem(223.189)};
  background: #b5dd71;

  &:focus-visible {
    outline: ${rem(2)} solid rgba(255, 251, 244, 0.9);
    outline-offset: ${rem(3)};
  }
  &:hover {
    background: #012604;
    color: #b5dd71;
  }

  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    line-height: 100%;
    letter-spacing: ${rem(-0.64)};
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
    border-radius: ${rem(431.177)};
  }
`;

const PlayPauseButton = styled.button`
  position: relative;
  display: inline-flex;
  width: ${rem(43)};
  height: ${rem(43)};
  padding: ${rem(12)} ${rem(16)};
  justify-content: center;
  align-items: center;

  border: 0;
  border-radius: ${rem(431.177)};
  background: rgba(255, 251, 244, 0.5);
  cursor: pointer;

  &:focus-visible {
    outline: ${rem(2)} solid rgba(255, 251, 244, 0.9);
    outline-offset: ${rem(3)};
  }

  /* keep icon above the ring */
  & > svg:last-child {
    position: relative;
    z-index: 2;
  }
`;

const CIRCLE_CIRCUMFERENCE = 131.947;

const ProgressRing = styled.svg.attrs<{ value: number }>((p) => ({
  style: {
    ['--progress-offset' as string]: CIRCLE_CIRCUMFERENCE - CIRCLE_CIRCUMFERENCE * p.value,
  } as React.CSSProperties,
}))`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: rotate(-90deg);
  z-index: 1;

  circle {
    fill: none;
    stroke: #012604;
    stroke-width: ${rem(1.2)};
    stroke-linecap: round;
    stroke-dasharray: ${CIRCLE_CIRCUMFERENCE};
    stroke-dashoffset: var(--progress-offset);
    transition: stroke-dashoffset 120ms linear;
  }
`;
